import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useGoogleLogin, useGoogleOAuth, googleLogout } from '@react-oauth/google';
import { AuthUser } from '../types';
import { ADMIN_EMAILS, AUTH_STORAGE_KEY } from '../lib/constants';

// Tempo máximo de espera pelo popup de renovação silenciosa. Se o popup for
// bloqueado pelo navegador ou o usuário não responder, resolvemos como falha
// para cair no fallback visível ("Sessão expirou — Reconectar").
const REFRESH_TIMEOUT_MS = 12000;

// GoogleOAuthProvider injeta o script do GIS (accounts.google.com/gsi/client)
// de forma assíncrona. Até `scriptLoadedSuccessfully` virar true, o client
// OAuth interno do useGoogleLogin ainda não existe e a função que ele retorna
// é um no-op silencioso — nem abre popup, nem dispara onError. Se o token
// salvo já expirou e o refresh automático dispara logo no primeiro load da
// página (antes do script terminar de carregar), essa chamada vira um no-op e
// só sobra o timeout de 12s (sem popup nenhum). Damos essa margem para o
// script carregar antes de acionar o client.
const SCRIPT_LOAD_TIMEOUT_MS = 5000;

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  signIn: () => void;
  signOut: () => void;
  /**
   * Tenta renovar o access token via Google sem interação explícita (o popup
   * costuma ser invisível quando a sessão Google ainda está ativa). Resolve com
   * o novo access token, ou `null` se falhou/expirou/timeout. Retornar o token
   * evita closures presas no token antigo no caller.
   */
  refreshSession: () => Promise<string | null>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const saved = localStorage.getItem(AUTH_STORAGE_KEY);
      return saved ? (JSON.parse(saved) as AuthUser) : null;
    } catch {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve da Promise de refreshSession em andamento (settle único: a primeira
  // de onSuccess/onError/timeout vence; as demais viram no-op). Resolve com o
  // novo token em sucesso, ou `null` em falha.
  const pendingRefresh = useRef<((token: string | null) => void) | null>(null);

  const settleRefresh = useCallback((token: string | null) => {
    const resolve = pendingRefresh.current;
    pendingRefresh.current = null;
    resolve?.(token);
  }, []);

  // Espera o script do GIS carregar antes de acionar o client OAuth — ver
  // comentário de SCRIPT_LOAD_TIMEOUT_MS acima. Resolve imediatamente se o
  // script já carregou (caso comum: usuário clicando bem depois do load).
  const { scriptLoadedSuccessfully } = useGoogleOAuth();
  const scriptWaiters = useRef<Array<() => void>>([]);

  useEffect(() => {
    if (!scriptLoadedSuccessfully) return;
    const waiters = scriptWaiters.current;
    scriptWaiters.current = [];
    waiters.forEach((notify) => notify());
  }, [scriptLoadedSuccessfully]);

  const waitForGoogleScript = useCallback(
    (timeoutMs: number): Promise<void> => {
      if (scriptLoadedSuccessfully) return Promise.resolve();
      return new Promise((resolve) => {
        const onReady = () => {
          clearTimeout(timer);
          resolve();
        };
        const timer = setTimeout(() => {
          scriptWaiters.current = scriptWaiters.current.filter((w) => w !== onReady);
          resolve();
        }, timeoutMs);
        scriptWaiters.current.push(onReady);
      });
    },
    [scriptLoadedSuccessfully],
  );

  const buildUser = useCallback(
    (p: { sub: string; name: string; email: string; picture?: string }, accessToken: string): AuthUser => {
      if (!p.email?.endsWith('@ciandt.com')) {
        throw new Error('Acesso restrito a colaboradores CI&T');
      }
      const isAdmin = ADMIN_EMAILS.includes(p.email);
      return {
        id: p.sub,
        name: p.name,
        email: p.email,
        avatar: p.picture,
        accessToken,
        isAdmin,
      };
    },
    [],
  );

  const completeSignIn = useCallback(
    (userData: AuthUser) => {
      setUser(userData);
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
    },
    [],
  );

  const googleSignIn = useGoogleLogin({
    onSuccess: async ({ access_token }) => {
      try {
        setIsLoading(true);
        setError(null);
        const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${access_token}` },
        });
        if (!res.ok) throw new Error('Falha ao buscar perfil');
        const p = await res.json();
        completeSignIn(buildUser(p, access_token));
        settleRefresh(access_token);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao autenticar';
        setError(msg);
        googleLogout();
        settleRefresh(null);
      } finally { setIsLoading(false); }
    },
    onError: () => {
      setError('Erro ao autenticar com Google');
      setIsLoading(false);
      settleRefresh(null);
    },
  });

  // Dev bypass: quando rodando em `vite dev` e `VITE_DEV_LOGIN_EMAIL` está
  // setado, pula o fluxo do Google e simula o login com aquele e-mail —
  // mantém o mesmo gate de `@ciandt.com` e checagem de admin. Em build de
  // produção `import.meta.env.DEV` é `false` e este caminho é eliminado.
  const devEmail =
    import.meta.env.DEV && typeof import.meta.env.VITE_DEV_LOGIN_EMAIL === 'string'
      ? import.meta.env.VITE_DEV_LOGIN_EMAIL.trim()
      : '';

  const signIn = useCallback(() => {
    if (devEmail) {
      try {
        setIsLoading(true);
        setError(null);
        const p = {
          sub: `dev-${devEmail}`,
          name: devEmail.split('@')[0],
          email: devEmail,
        };
        completeSignIn(buildUser(p, 'dev-bypass-token'));
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao autenticar';
        setError(msg);
      } finally {
        setIsLoading(false);
      }
      return;
    }
    // Se o script do GIS ainda não carregou, googleSignIn() seria um no-op
    // silencioso (ver comentário de SCRIPT_LOAD_TIMEOUT_MS). Espera antes de
    // acionar — sem espera, hoje o clique simplesmente não faz nada.
    void waitForGoogleScript(SCRIPT_LOAD_TIMEOUT_MS).then(() => googleSignIn());
  }, [devEmail, googleSignIn, buildUser, completeSignIn, waitForGoogleScript]);

  const signOut = useCallback(() => {
    googleLogout();
    // GIS escreve `g_state` (JSON com base64) no domínio do app pra lembrar
    // que o usuário deslogou. Esse valor dispara regra OWASP CRS no AFD WAF
    // (X-Azure-Ref ...) e o próximo request bloqueia com 403. Apaga antes de
    // qualquer navegação subsequente.
    document.cookie = "g_state=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
    setUser(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }, []);

  const refreshSession = useCallback((): Promise<string | null> => {
    // Dev bypass: o token fake nunca expira de verdade — re-emite na hora.
    if (devEmail) {
      try {
        const p = { sub: `dev-${devEmail}`, name: devEmail.split('@')[0], email: devEmail };
        completeSignIn(buildUser(p, 'dev-bypass-token'));
        return Promise.resolve('dev-bypass-token');
      } catch {
        return Promise.resolve(null);
      }
    }

    return new Promise<string | null>((resolve) => {
      const timer = setTimeout(() => settleRefresh(null), REFRESH_TIMEOUT_MS);
      // Limpa o timer assim que a Promise resolver por qualquer via (sucesso,
      // erro do popup ou o próprio timeout).
      pendingRefresh.current = (token: string | null) => {
        clearTimeout(timer);
        resolve(token);
      };
      // Se o script do GIS ainda não carregou (comum logo no primeiro load da
      // página), googleSignIn() seria um no-op silencioso — nem popup, nem
      // onError — e essa Promise só resolveria pelo timeout de 12s acima, sem
      // nunca ter dado ao usuário a chance de reautenticar. Espera o script
      // (janela menor que o timeout total, para sobrar tempo de popup) antes
      // de acionar o client.
      void waitForGoogleScript(SCRIPT_LOAD_TIMEOUT_MS).then(() => {
        if (pendingRefresh.current) googleSignIn();
      });
    });
  }, [devEmail, googleSignIn, buildUser, completeSignIn, settleRefresh, waitForGoogleScript]);

  return (
    <AuthContext.Provider value={{ user, isLoading, signIn, signOut, refreshSession, error }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
